import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Plus, Trash2, Upload, Camera, ChevronRight, ChevronLeft, Check, User, Building2, MapPin, Phone, CreditCard, Lock, Save, CheckCircle2, XCircle, Power, ShieldCheck, Key, RefreshCw, Eye, EyeOff, UserCheck, AlertCircle, Shield } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GoogleGenAI } from '@google/genai';
import { isPessoaAtiva } from '../services/pessoaService';
import { pessoaCascadeService } from '../services/pessoaCascadeService';
import { storageService } from '../services/storageService';
import { fiscalService } from '../services/fiscalService';
import { empresaService } from '../services/empresaService';
import { userManagementService } from '../services/userManagementService';

interface PessoasFormProps {
  pessoaId?: number | null;
  onClose: () => void;
  onSave: () => void;
}

export function PessoasForm({ pessoaId, onClose, onSave }: PessoasFormProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [tipoPessoa, setTipoPessoa] = useState<'PF' | 'PJ'>('PF');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const steps = [
    { id: 'gerais', label: 'Dados Gerais', icon: User },
    { id: 'contatos', label: 'Contatos', icon: Phone },
    { id: 'enderecos', label: 'Endereços', icon: MapPin },
    { id: 'bancarios', label: 'Dados Bancários', icon: CreditCard },
    { id: 'acesso', label: 'Acesso', icon: Lock }
  ];

  // Lookups
  const [tiposCliente, setTiposCliente] = useState<any[]>([]);
  const [categoriasCliente, setCategoriasCliente] = useState<any[]>([]);
  const [gruposPessoa, setGruposPessoa] = useState<any[]>([]);
  const [estados, setEstados] = useState<any[]>([]);
  const [cidades, setCidades] = useState<any[]>([]);
  const [bancos, setBancos] = useState<any[]>([]);
  const [formasPagamento, setFormasPagamento] = useState<any[]>([]);

  // Form Data
  const [formData, setFormData] = useState({
    id: '',
    nome: '',
    nome_fantasia: '',
    documento: '',
    tipo_cliente_id: '',
    categoria_cliente_id: '',
    email: '',
    fone: '',
    obs: '',
    numero_cnh: '',
    categoria_cnh: '',
    vencimento_cnh: '',
    documento_cnh: '',
    cnae: '',
    situacao_cadastral: '',
    data_inicio_atividade: '',
    ativo: true,
    aceita_notificacao_whatsapp: false,
    aceita_notificacao_email: false
  });

  const [selectedGrupos, setSelectedGrupos] = useState<number[]>([]);
  const [contatos, setContatos] = useState<any[]>([]);
  const [enderecos, setEnderecos] = useState<any[]>([]);
  const [dadosBancarios, setDadosBancarios] = useState<any[]>([]);

  // New item states
  const [newContato, setNewContato] = useState({ nome: '', email: '', telefone: '', obs: '' });
  const [newEndereco, setNewEndereco] = useState({ nome: '', principal: 'N', cep: '', estado_id: '', cidade_id: '', rua: '', numero: '', bairro: '', complemento: '' });
  const [newDadoBancario, setNewDadoBancario] = useState({ forma_pagamento_id: '', banco_id: '', conta: '', agencia: '', nome: '', cpf: '', pix: '' });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isAnalyzingCnh, setIsAnalyzingCnh] = useState(false);

  // --- Access Tab States & Logic ---
  const [linkedUsers, setLinkedUsers] = useState<any[]>([]);
  const [selectedUserIdx, setSelectedUserIdx] = useState<number>(0);
  const [matchedByNameOnly, setMatchedByNameOnly] = useState<boolean>(false);
  const [linkedUserGroups, setLinkedUserGroups] = useState<any[]>([]);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [allSystemGroups, setAllSystemGroups] = useState<any[]>([]);

  const linkedUser = selectedUserIdx >= 0 && linkedUsers[selectedUserIdx] ? linkedUsers[selectedUserIdx] : null;

  const [accessForm, setAccessForm] = useState({
    name: '',
    login: '',
    email: '',
    password: 'Voope@2025',
    groupId: 5,
    showPassword: false
  });
  const [isSavingAccess, setIsSavingAccess] = useState(false);
  const [accessMsg, setAccessMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [editingGroupId, setEditingGroupId] = useState<number>(5);
  const [newPasswordEdit, setNewPasswordEdit] = useState('');
  const [showNewPasswordEdit, setShowNewPasswordEdit] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    if (currentStep === 4) {
      checkAccessAccount();
    }
  }, [currentStep]);

  async function loadGroupsForUser(userId: number, groupsList = allSystemGroups) {
    const { data: uGroups } = await supabase
      .from('system_user_group')
      .select('system_group_id, system_group(id, name)')
      .eq('system_user_id', userId);

    const groupList = (uGroups || []).map((ug: any) => ({
      id: ug.system_group_id,
      name: ug.system_group?.name || groupsList.find(g => g.id === ug.system_group_id)?.name || `Grupo #${ug.system_group_id}`
    }));
    setLinkedUserGroups(groupList);
    if (groupList.length > 0) {
      setEditingGroupId(groupList[0].id);
    } else {
      setEditingGroupId(5);
    }
  }

  function resetAccessForm(docToSearch: string) {
    let defaultGrp = 5;
    const motoristaGrupo = gruposPessoa.find(g => (g.nome || '').toLowerCase().includes('motorista'));
    const clienteGrupo = gruposPessoa.find(g => (g.nome || '').toLowerCase().includes('cliente'));

    if (motoristaGrupo && selectedGrupos.includes(motoristaGrupo.id)) {
      defaultGrp = 7;
    } else if (clienteGrupo && selectedGrupos.includes(clienteGrupo.id)) {
      defaultGrp = 6;
    }

    setAccessForm({
      name: formData.nome || '',
      login: formData.email ? formData.email.split('@')[0] : (docToSearch || (formData.nome || '').toLowerCase().replace(/\s+/g, '.')),
      email: formData.email || '',
      password: 'Voope@2025',
      groupId: defaultGrp,
      showPassword: false
    });
  }

  async function checkAccessAccount() {
    setIsCheckingAccess(true);
    setAccessMsg(null);
    try {
      const groups = await userManagementService.getSystemGroups();
      setAllSystemGroups(groups);

      // Escapa curingas do LIKE/ILIKE ('%' e '_') para que o texto digitado seja
      // tratado como literal, nunca como padrão de busca.
      const escapeIlike = (s: string) => s.replace(/[%_]/g, (c) => `\\${c}`);

      const emailToSearch = (formData.email || '').trim().toLowerCase();
      const docToSearch = (formData.documento || '').replace(/\D/g, '');
      const nameToSearch = (formData.nome || '').trim().toLowerCase();

      const foundUsersMap = new Map<number, any>();
      let matchedByNameOnly = false;

      if (emailToSearch) {
        const { data } = await supabase
          .from('system_users')
          .select('*')
          .ilike('email', escapeIlike(emailToSearch));
        if (data) data.forEach(u => foundUsersMap.set(u.id, u));
      }

      if (docToSearch) {
        const { data } = await supabase
          .from('system_users')
          .select('*')
          .ilike('login', escapeIlike(docToSearch));
        if (data) data.forEach(u => foundUsersMap.set(u.id, u));
      }

      // IMPORTANTE: casar só pelo nome é o último recurso — nomes duplicados são
      // comuns e isso pode linkar a conta de OUTRA pessoa. Sinalizamos isso na UI
      // (matchedByNameOnly) para o operador confirmar antes de mudar grupo/senha.
      if (nameToSearch && foundUsersMap.size === 0) {
        const { data } = await supabase
          .from('system_users')
          .select('*')
          .ilike('name', escapeIlike(nameToSearch));
        if (data && data.length > 0) {
          matchedByNameOnly = true;
          data.forEach(u => foundUsersMap.set(u.id, u));
        }
      }

      const usersList = Array.from(foundUsersMap.values());
      setLinkedUsers(usersList);
      setMatchedByNameOnly(matchedByNameOnly);

      if (usersList.length > 0) {
        setSelectedUserIdx(0);
        await loadGroupsForUser(usersList[0].id, groups);
      } else {
        setSelectedUserIdx(-1);
        setLinkedUserGroups([]);
        resetAccessForm(docToSearch);
      }
    } catch (err) {
      console.error('Erro ao verificar conta de acesso:', err);
    } finally {
      setIsCheckingAccess(false);
    }
  }

  const handleSelectUserIndex = async (idx: number) => {
    setSelectedUserIdx(idx);
    setAccessMsg(null);
    setNewPasswordEdit('');
    if (idx >= 0 && linkedUsers[idx]) {
      await loadGroupsForUser(linkedUsers[idx].id);
    } else {
      const docToSearch = (formData.documento || '').replace(/\D/g, '');
      resetAccessForm(docToSearch);
    }
  };

  const handleCreateAccess = async () => {
    if (!accessForm.email || !accessForm.name || !accessForm.login) {
      setAccessMsg({ type: 'error', text: 'Preencha o Nome, Login e E-mail para criar a conta de acesso.' });
      return;
    }

    setIsSavingAccess(true);
    setAccessMsg(null);

    try {
      const res = await userManagementService.criarUsuario({
        name: accessForm.name,
        login: accessForm.login,
        email: accessForm.email,
        password: accessForm.password,
        role: 'user',
        groupIds: [accessForm.groupId]
      });

      if (res.success) {
        setAccessMsg({ type: 'success', text: 'Conta de acesso criada com sucesso!' });
        if (!formData.email && accessForm.email) {
          setFormData(prev => ({ ...prev, email: accessForm.email }));
        }
        await checkAccessAccount();
      } else {
        setAccessMsg({ type: 'error', text: res.message || 'Erro ao criar conta de acesso.' });
      }
    } catch (err: any) {
      setAccessMsg({ type: 'error', text: err.message || 'Erro ao criar conta.' });
    } finally {
      setIsSavingAccess(false);
    }
  };

  const handleToggleAccessStatus = async () => {
    if (!linkedUser) return;
    setIsSavingAccess(true);
    try {
      const success = await userManagementService.toggleUserStatus(linkedUser.id, linkedUser.active);
      if (success) {
        const newStatus = (linkedUser.active === 'Y' || linkedUser.active === 'S') ? 'N' : 'Y';
        setLinkedUsers(prev => prev.map((u, i) => i === selectedUserIdx ? { ...u, active: newStatus } : u));
        setAccessMsg({
          type: 'success',
          text: `Acesso do usuário ${newStatus === 'Y' ? 'ativado' : 'inativado'} com sucesso!`
        });
      } else {
        setAccessMsg({ type: 'error', text: 'Erro ao alterar status do usuário.' });
      }
    } catch (err: any) {
      setAccessMsg({ type: 'error', text: err.message || 'Erro ao alterar status.' });
    } finally {
      setIsSavingAccess(false);
    }
  };

  const handleUpdateUserGroup = async () => {
    if (!linkedUser) return;
    setIsSavingAccess(true);
    try {
      await supabase.from('system_user_group').delete().eq('system_user_id', linkedUser.id);
      await supabase.from('system_user_group').insert({
        system_user_id: linkedUser.id,
        system_group_id: editingGroupId
      });
      setAccessMsg({ type: 'success', text: 'Perfil de acesso (grupo) atualizado com sucesso!' });
      await checkAccessAccount();
    } catch (err: any) {
      setAccessMsg({ type: 'error', text: err.message || 'Erro ao atualizar grupo de acesso.' });
    } finally {
      setIsSavingAccess(false);
    }
  };

  const handleChangePassword = async () => {
    if (!linkedUser || !newPasswordEdit || newPasswordEdit.length < 6) {
      setAccessMsg({ type: 'error', text: 'A senha deve possuir pelo menos 6 caracteres.' });
      return;
    }
    setIsChangingPassword(true);
    try {
      const res = await userManagementService.atualizarDadosUsuario(
        linkedUser.id,
        linkedUser.auth_user_id,
        {
          name: linkedUser.name,
          login: linkedUser.login,
          email: linkedUser.email || '',
          password: newPasswordEdit
        }
      );

      if (res.success) {
        setAccessMsg({ type: 'success', text: 'Senha do usuário redefinida com sucesso!' });
        setNewPasswordEdit('');
      } else {
        setAccessMsg({ type: 'error', text: res.message || 'Erro ao redefinir senha.' });
      }
    } catch (err: any) {
      setAccessMsg({ type: 'error', text: err.message || 'Erro ao redefinir senha.' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccess = async () => {
    if (!linkedUser) return;
    if (!window.confirm(`Tem certeza que deseja remover o acesso do usuário ${linkedUser.name}?`)) return;

    setIsSavingAccess(true);
    try {
      const success = await userManagementService.excluirUsuario(linkedUser.id);
      if (success) {
        setAccessMsg({ type: 'success', text: 'Conta de acesso removida com sucesso.' });
        setLinkedUserGroups([]);
        await checkAccessAccount();
      } else {
        setAccessMsg({ type: 'error', text: 'Erro ao remover conta de acesso.' });
      }
    } catch (err: any) {
      setAccessMsg({ type: 'error', text: err.message || 'Erro ao remover conta.' });
    } finally {
      setIsSavingAccess(false);
    }
  };

  const resolveTipoClienteId = (tipo: 'PF' | 'PJ', list: any[]): string => {
    if (!list || list.length === 0) return tipo === 'PJ' ? '2' : '1';
    
    const normalize = (s: string) => (s || '').normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
    
    if (tipo === 'PJ') {
      const found = list.find(t => {
        const nome = normalize(t.nome);
        const sigla = normalize(t.sigla);
        return sigla === 'pj' || nome.includes('juridica') || nome.includes('pj') || nome.includes('empresa') || nome.includes('comercial');
      });
      if (found) return found.id.toString();
      const item2 = list.find(t => t.id === 2);
      if (item2) return item2.id.toString();
      if (list.length > 1) return list[1].id.toString();
      return list[0].id.toString();
    } else {
      const found = list.find(t => {
        const nome = normalize(t.nome);
        const sigla = normalize(t.sigla);
        return sigla === 'pf' || nome.includes('fisica') || nome.includes('pf') || nome.includes('individual');
      });
      if (found) return found.id.toString();
      const item1 = list.find(t => t.id === 1);
      if (item1) return item1.id.toString();
      return list[0].id.toString();
    }
  };

  useEffect(() => {
    fetchLookups();
    if (pessoaId) {
      fetchPessoa(pessoaId);
    }
  }, [pessoaId]);

  async function fetchLookups() {
    try {
      const [
        { data: tipos },
        { data: categorias },
        { data: grupos },
        { data: ests },
        { data: bcs },
        { data: formas }
      ] = await Promise.all([
        supabase.from('tipo_cliente').select('*').order('nome'),
        supabase.from('categoria_cliente').select('*').order('nome'),
        supabase.from('grupo_pessoa').select('*').order('nome'),
        supabase.from('estado').select('*').order('nome'),
        supabase.from('banco').select('*').order('nome'),
        supabase.from('forma_pagamento').select('*').order('nome')
      ]);

      const tiposList = tipos || [];
      setTiposCliente(tiposList);
      setCategoriasCliente(categorias || []);
      setGruposPessoa(grupos || []);
      setEstados(ests || []);
      setBancos(bcs || []);
      setFormasPagamento(formas || []);

      // Auto-assign default tipo_cliente_id if not editing existing
      if (!pessoaId) {
        setFormData(prev => {
          if (!prev.tipo_cliente_id) {
            return { ...prev, tipo_cliente_id: resolveTipoClienteId(tipoPessoa, tiposList) };
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error fetching lookups:', error);
    }
  }

  async function fetchCidades(estadoId: string | number) {
    try {
      const { data } = await supabase.from('cidade').select('*').eq('estado_id', estadoId).order('nome');
      setCidades(data || []);
    } catch (error) {
      console.error('Error fetching cidades:', error);
    }
  }

  async function fetchPessoa(id: number) {
    setIsLoading(true);
    try {
      const { data: pessoa, error } = await supabase.from('pessoa').select('*').eq('id', id).single();
      if (error) throw error;

      setFormData({
        id: pessoa.id.toString(),
        nome: pessoa.nome || '',
        nome_fantasia: pessoa.nome_fantasia || '',
        documento: pessoa.documento || '',
        tipo_cliente_id: pessoa.tipo_cliente_id?.toString() || '',
        categoria_cliente_id: pessoa.categoria_cliente_id?.toString() || '',
        email: pessoa.email || '',
        fone: pessoa.fone || '',
        obs: pessoa.obs || '',
        numero_cnh: pessoa.numero_cnh || '',
        categoria_cnh: pessoa.categoria_cnh || '',
        vencimento_cnh: pessoa.vencimento_cnh || '',
        documento_cnh: pessoa.documento_cnh || '',
        cnae: pessoa.cnae || '',
        situacao_cadastral: pessoa.situacao_cadastral || '',
        data_inicio_atividade: pessoa.data_inicio_atividade || '',
        ativo: isPessoaAtiva(pessoa),
        aceita_notificacao_whatsapp: !!pessoa.aceita_notificacao_whatsapp,
        aceita_notificacao_email: !!pessoa.aceita_notificacao_email
      });

      if (pessoa.documento) {
        const digits = pessoa.documento.replace(/\D/g, '');
        if (digits.length > 11) setTipoPessoa('PJ');
        else setTipoPessoa('PF');
      } else if (pessoa.tipo_cliente_id) {
        const tipo = tiposCliente?.find(t => t.id === pessoa.tipo_cliente_id);
        if (tipo) {
          const nome = tipo.nome.toLowerCase();
          if (nome.includes('jurídica') || nome === 'pj') setTipoPessoa('PJ');
          else setTipoPessoa('PF');
        }
      }

      // Fetch related data safely without letting individual query failures crash the entire form
      let gruposData: any[] = [];
      let contsData: any[] = [];
      let endsData: any[] = [];
      let bancsData: any[] = [];

      try {
        const { data: g } = await supabase.from('pessoa_grupo').select('grupo_pessoa_id').eq('pessoa_id', id);
        if (g) gruposData = g;
      } catch (e) {
        console.warn('Erro ao carregar grupos:', e);
      }

      try {
        const { data: c } = await supabase.from('pessoa_contato').select('*').eq('pessoa_id', id);
        if (c) contsData = c;
      } catch (e) {
        console.warn('Erro ao carregar contatos:', e);
      }

      try {
        let { data: e, error: eErr } = await supabase.from('pessoa_endereco').select('*, cidade:cidade_id(nome, estado_id)').eq('pessoa_id', id);
        if (eErr || !e) {
          const fallback = await supabase.from('pessoa_endereco').select('*').eq('pessoa_id', id);
          e = fallback.data;
        }
        if (e) endsData = e;
      } catch (e) {
        console.warn('Erro ao carregar enderecos:', e);
      }

      try {
        let { data: b, error: bErr } = await supabase.from('dados_bancarios').select('*, banco:banco_id(nome), forma_pagamento:forma_pagamento_id(nome)').eq('pessoa_id', id);
        if (bErr || !b) {
          const fallback = await supabase.from('dados_bancarios').select('*').eq('pessoa_id', id);
          b = fallback.data;
        }
        if (b) bancsData = b;
      } catch (e) {
        console.warn('Erro ao carregar dados bancarios:', e);
      }

      setSelectedGrupos(gruposData.map(g => g.grupo_pessoa_id).filter(Boolean));
      setContatos(contsData);
      setEnderecos(endsData);
      setDadosBancarios(bancsData);

    } catch (error: any) {
      console.error('Error fetching pessoa:', error);
      alert('Erro ao carregar os dados da pessoa: ' + (error?.message || ''));
    } finally {
      setIsLoading(false);
    }
  }

  const formatCpfCnpj = (value: string) => {
    const v = value.replace(/\D/g, '');
    if (v.length <= 11) {
      return v.replace(/(\d{3})(\d)/, '$1.$2')
              .replace(/(\d{3})(\d)/, '$1.$2')
              .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      return v.replace(/^(\d{2})(\d)/, '$1.$2')
              .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
              .replace(/\.(\d{3})(\d)/, '.$1/$2')
              .replace(/(\d{4})(\d)/, '$1-$2')
              .slice(0, 18);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'documento') {
      const formatted = formatCpfCnpj(value);
      const digits = formatted.replace(/\D/g, '');
      const newTipo = digits.length > 11 ? 'PJ' : 'PF';
      
      if (newTipo !== tipoPessoa) {
        setTipoPessoa(newTipo);
        const resolvedId = resolveTipoClienteId(newTipo, tiposCliente);
        setFormData(prev => ({ ...prev, documento: formatted, tipo_cliente_id: resolvedId }));
        return;
      }
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleGrupoChange = (grupoId: number) => {
    setSelectedGrupos(prev => 
      prev.includes(grupoId) ? prev.filter(id => id !== grupoId) : [...prev, grupoId]
    );
  };

  const isPJ = tipoPessoa === 'PJ';
  const docLabel = isPJ ? 'CNPJ' : 'CPF';
  const docPlaceholder = isPJ ? '00.000.000/0000-00' : '000.000.000-00';

  const isMotorista = () => {
    const motoristaGrupo = gruposPessoa.find(g => g.nome.toLowerCase() === 'motorista');
    return motoristaGrupo && selectedGrupos.includes(motoristaGrupo.id);
  };

  const handleTipoPessoaChange = (tipo: 'PF' | 'PJ') => {
    setTipoPessoa(tipo);
    const resolvedId = resolveTipoClienteId(tipo, tiposCliente);
    setFormData(prev => ({ ...prev, tipo_cliente_id: resolvedId }));
  };

  // --- Helper to resolve or create Estado/Cidade ---
  const normalizeString = (str: string) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();

  const resolveEstadoCidade = async (uf: string, municipio: string, codigoIbge?: string) => {
    let estadoId = '';
    let cidadeId = '';

    // 1. Resolve Estado
    let estado = estados.find(e => e.sigla.toUpperCase() === uf.toUpperCase());
    if (!estado) {
      const { data: estData } = await supabase.from('estado').select('*').ilike('sigla', uf).single();
      if (estData) {
        estado = estData;
        setEstados(prev => [...prev, estData]);
      } else {
        const { data: newEst, error } = await supabase.from('estado').insert({ nome: uf, sigla: uf }).select().single();
        if (!error && newEst) {
          estado = newEst;
          setEstados(prev => [...prev, newEst]);
        }
      }
    }
    
    if (estado) {
      estadoId = estado.id.toString();
      
      // 2. Resolve Cidade
      let { data: cidadesData } = await supabase.from('cidade').select('*').eq('estado_id', estado.id).order('nome');
      let cidade = cidadesData?.find(c => normalizeString(c.nome) === normalizeString(municipio));
      
      if (!cidade) {
        const { data: newCid, error } = await supabase.from('cidade').insert({ 
          nome: municipio, 
          estado_id: estado.id,
          codigo_ibge: codigoIbge || '0000000'
        }).select().single();
        if (error) {
          console.error('Erro ao cadastrar cidade:', error);
        }
        if (!error && newCid) {
          cidade = newCid;
          if (cidadesData) cidadesData.push(newCid);
        }
      }
      
      if (cidadesData) {
        setCidades([...cidadesData]);
      } else if (cidade) {
        setCidades([cidade]);
      } else {
        setCidades([]);
      }
      
      if (cidade) {
        cidadeId = cidade.id.toString();
      }
    }

    return { estadoId, cidadeId };
  };

  // --- Focus NFe CNPJ ---
  // Este handler foi escrito originalmente para a BrasilAPI (por isso os
  // nomes "API Brasil" no comentário/alerta) e depois passou a chamar a
  // Focus NFe sem atualizar os campos lidos da resposta. A Focus devolve um
  // formato bem mais enxuto que a BrasilAPI: sem telefone, e-mail, descrição
  // de CNAE/situação cadastral ou data de abertura - só razão social,
  // situação (código), CNAE (código) e o endereço aninhado em "endereco".
  // Preenche só o que a Focus realmente manda; o resto mantém o que já
  // estava digitado no formulário.
  const buscarCNPJ = async () => {
    const cnpj = formData.documento.replace(/\D/g, '');
    if (cnpj.length !== 14) {
      alert('CNPJ inválido. Digite 14 números.');
      return;
    }
    try {
      const data = await fiscalService.consultarCnpjFocus(cnpj);
      if (!data || !data.razao_social) throw new Error('CNPJ não encontrado na base da Receita / Focus NFe');

      const endereco = data.endereco || {};

      setTipoPessoa('PJ');
      const resolvedTipoId = resolveTipoClienteId('PJ', tiposCliente);

      setFormData(prev => ({
        ...prev,
        nome: data.razao_social || prev.nome,
        nome_fantasia: data.nome_fantasia || prev.nome_fantasia,
        tipo_cliente_id: resolvedTipoId,
        cnae: data.cnae_principal || prev.cnae,
        situacao_cadastral: data.situacao_cadastral || prev.situacao_cadastral,
      }));

      // Auto-fill address fields
      if (endereco.cep) {
        const { estadoId, cidadeId } = await resolveEstadoCidade(endereco.uf, endereco.nome_municipio, endereco.codigo_ibge);
        setNewEndereco(prev => ({
          ...prev,
          nome: 'Sede',
          principal: 'S',
          cep: endereco.cep,
          rua: [endereco.tipo_logradouro, endereco.logradouro].filter(Boolean).join(' ') || prev.rua,
          bairro: endereco.bairro || prev.bairro,
          numero: endereco.numero || prev.numero,
          complemento: endereco.complemento || prev.complemento,
          estado_id: estadoId,
          cidade_id: cidadeId
        }));
      }
    } catch (error) {
      console.error(error);
      alert('Erro ao buscar CNPJ na Focus NFe.');
    }
  };

  // --- ViaCEP ---
  // Usa o mesmo empresaService.buscarCep() (ViaCEP direto, sem Edge Function)
  // já testado em produção na tela de Dados da Empresa - por decisão prévia,
  // CEP fica no ViaCEP mesmo com CNPJ/Município/CFOP na Focus NFe. Esta
  // função aqui chamava fiscal-auxiliar (Focus) com nomes de campo que nem
  // batiam com a resposta real, então sempre falhava.
  const buscarCEP = async (cepOverride?: string) => {
    const cep = (cepOverride || newEndereco.cep).replace(/\D/g, '');
    if (cep.length !== 8) {
      if (!cepOverride) alert('CEP inválido. Digite 8 números.');
      return;
    }
    try {
      const data = await empresaService.buscarCep(cep);
      if (data.error) throw new Error(data.error);

      const { estadoId, cidadeId } = await resolveEstadoCidade(data.uf, data.cidade, data.ibge);

      setNewEndereco(prev => ({
        ...prev,
        cep: cepOverride || prev.cep,
        rua: data.logradouro || prev.rua,
        bairro: data.bairro || prev.bairro,
        estado_id: estadoId,
        cidade_id: cidadeId
      }));
    } catch (error) {
      console.error(error);
      if (!cepOverride) alert('Erro ao buscar CEP.');
    }
  };

  // --- Gemini CNH Analysis ---
  const handleCnhUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsAnalyzingCnh(true);
    try {
      // 1. Upload to Supabase Storage (Bucket: sistema/cnh)
      const { publicUrl: fileUrl } = await storageService.uploadFile(file, 'cnh');

      // 2. Convert file to base64 for Gemini
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        const base64Data = (reader.result as string).split(',')[1];
        
        // Save URL to form data to display/save later
        setFormData(prev => ({ ...prev, documento_cnh: fileUrl }));

        // Call Gemini
        const apiKey = process.env.GEMINI_API_KEY;
        if (!apiKey) {
          console.error("GEMINI_API_KEY not found.");
          alert('Erro: Chave da API Gemini não configurada.');
          setIsAnalyzingCnh(false);
          return;
        }
        const ai = new GoogleGenAI({ apiKey });

        const response = await ai.models.generateContent({
          model: 'gemini-3-flash-preview',
          contents: {
            parts: [
              {
                inlineData: {
                  data: base64Data,
                  mimeType: file.type,
                },
              },
              {
                text: 'Extraia os seguintes dados desta CNH brasileira e retorne APENAS um JSON válido com as chaves: "numero_cnh" (string), "categoria_cnh" (string), "vencimento_cnh" (string no formato YYYY-MM-DD), "nome" (string), "cpf" (string). Se não encontrar, deixe vazio.',
              },
            ],
          },
          config: {
            responseMimeType: "application/json",
          }
        });

        if (response.text) {
          console.log("Gemini response:", response.text);
          try {
            const data = JSON.parse(response.text);
            console.log("Parsed Gemini data:", data);
            setFormData(prev => ({
              ...prev,
              numero_cnh: data.numero_cnh || prev.numero_cnh,
              categoria_cnh: data.categoria_cnh || prev.categoria_cnh,
              vencimento_cnh: data.vencimento_cnh || prev.vencimento_cnh,
              nome: data.nome || prev.nome,
              documento: data.cpf || prev.documento
            }));
          } catch (e) {
            console.error("Failed to parse Gemini JSON", e);
          }
        }
        setIsAnalyzingCnh(false);
      };
    } catch (error: any) {
      console.error('Error analyzing CNH:', error);
      alert('Erro ao analisar a CNH com IA: ' + error.message);
      setIsAnalyzingCnh(false);
    }
  };

  // --- List Handlers ---
  const addContato = () => {
    if (!newContato.nome) return;
    setContatos([...contatos, { ...newContato, id: Date.now() }]); // Temp ID
    setNewContato({ nome: '', email: '', telefone: '', obs: '' });
  };
  const removeContato = (index: number) => setContatos(contatos.filter((_, i) => i !== index));

  const addEndereco = () => {
    if (!newEndereco.nome || !newEndereco.cidade_id) return;
    const cidade = cidades.find(c => c.id.toString() === newEndereco.cidade_id);
    setEnderecos([...enderecos, { ...newEndereco, id: Date.now(), cidade }]); // Temp ID
    setNewEndereco({ nome: '', principal: 'N', cep: '', estado_id: '', cidade_id: '', rua: '', numero: '', bairro: '', complemento: '' });
  };
  const removeEndereco = (index: number) => setEnderecos(enderecos.filter((_, i) => i !== index));

  const addDadoBancario = () => {
    if (!newDadoBancario.banco_id || !newDadoBancario.forma_pagamento_id) return;
    const banco = bancos.find(b => b.id.toString() === newDadoBancario.banco_id);
    const forma = formasPagamento.find(f => f.id.toString() === newDadoBancario.forma_pagamento_id);
    setDadosBancarios([...dadosBancarios, { ...newDadoBancario, id: Date.now(), banco, forma_pagamento: forma }]); // Temp ID
    setNewDadoBancario({ forma_pagamento_id: '', banco_id: '', conta: '', agencia: '', nome: '', cpf: '', pix: '' });
  };
  const removeDadoBancario = (index: number) => setDadosBancarios(dadosBancarios.filter((_, i) => i !== index));

  // --- Save ---
  const handleSave = async () => {
    const nomeVal = (formData.nome || '').trim();
    const docVal = (formData.documento || '').trim();

    if (!nomeVal) {
      alert(tipoPessoa === 'PJ' ? 'Preencha a Razão Social.' : 'Preencha o Nome.');
      setCurrentStep(0);
      return;
    }

    if (!docVal) {
      alert(tipoPessoa === 'PJ' ? 'Preencha o CNPJ.' : 'Preencha o CPF.');
      setCurrentStep(0);
      return;
    }

    const docLimpo = docVal.replace(/\D/g, '');
    if (docLimpo.length !== 11 && docLimpo.length !== 14) {
      alert('Documento inválido. O CPF deve ter 11 dígitos e o CNPJ 14 dígitos.');
      setCurrentStep(0);
      return;
    }

    // Auto-resolve tipo_cliente_id if empty
    const resolvedTipoId = formData.tipo_cliente_id 
      ? formData.tipo_cliente_id 
      : resolveTipoClienteId(docLimpo.length > 11 ? 'PJ' : 'PF', tiposCliente);

    const parseDateToISO = (dateStr: string) => {
      if (!dateStr) return null;
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const [day, month, year] = parts;
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
      return dateStr;
    };

    setIsSaving(true);

    try {
      const isAtivo = formData.ativo;
      const parsedTipoClienteId = parseInt(resolvedTipoId) || (tipoPessoa === 'PJ' ? 2 : 1);
      const parsedCategoriaId = formData.categoria_cliente_id ? parseInt(formData.categoria_cliente_id) : null;

      const pessoaPayload: any = {
        nome: nomeVal,
        nome_fantasia: formData.nome_fantasia?.trim() || null,
        documento: docVal,
        tipo_cliente_id: parsedTipoClienteId,
        categoria_cliente_id: isNaN(parsedCategoriaId as number) ? null : parsedCategoriaId,
        email: formData.email?.trim() || null,
        fone: formData.fone?.trim() || null,
        obs: formData.obs?.trim() || null,
        numero_cnh: formData.numero_cnh || null,
        categoria_cnh: formData.categoria_cnh || null,
        vencimento_cnh: parseDateToISO(formData.vencimento_cnh),
        documento_cnh: formData.documento_cnh || null,
        cnae: formData.cnae || null,
        data_inicio_atividade: parseDateToISO(formData.data_inicio_atividade),
        situacao_cadastral: isAtivo ? (formData.situacao_cadastral || 'ATIVO') : 'INATIVO',
        ativo: isAtivo,
        data_desativacao: isAtivo ? null : new Date().toISOString(),
        aceita_notificacao_whatsapp: formData.aceita_notificacao_whatsapp,
        aceita_notificacao_email: formData.aceita_notificacao_email,
        updated_at: new Date().toISOString()
      };

      let savedPessoaId = pessoaId;

      if (pessoaId) {
        const { data: existingPessoa } = await supabase.from('pessoa').select('created_at').eq('id', pessoaId).single();
        const payloadToSave = {
          ...pessoaPayload,
          created_at: existingPessoa?.created_at || new Date().toISOString()
        };
        
        let { error } = await supabase.from('pessoa').update(payloadToSave).eq('id', pessoaId);
        
        // Graceful retry without extended columns if schema doesn't have them
        if (error) {
          console.warn('[PessoasForm] Retrying update with base columns:', error.message);
          const basePayload = {
            nome: nomeVal,
            nome_fantasia: formData.nome_fantasia?.trim() || null,
            documento: docVal,
            tipo_cliente_id: parsedTipoClienteId,
            categoria_cliente_id: isNaN(parsedCategoriaId as number) ? null : parsedCategoriaId,
            email: formData.email?.trim() || null,
            fone: formData.fone?.trim() || null,
            obs: formData.obs?.trim() || null,
            situacao_cadastral: isAtivo ? 'ATIVO' : 'INATIVO',
            updated_at: new Date().toISOString()
          };
          const { error: retryErr } = await supabase.from('pessoa').update(basePayload).eq('id', pessoaId);
          if (retryErr) throw retryErr;
        }
      } else {
        let { data, error } = await supabase.from('pessoa').insert([{ ...pessoaPayload, created_at: new Date().toISOString() }]).select().single();
        
        // Graceful retry without extended columns if schema doesn't have them
        if (error) {
          console.warn('[PessoasForm] Retrying insert with base columns:', error.message);
          const basePayload = {
            nome: nomeVal,
            nome_fantasia: formData.nome_fantasia?.trim() || null,
            documento: docVal,
            tipo_cliente_id: parsedTipoClienteId,
            categoria_cliente_id: isNaN(parsedCategoriaId as number) ? null : parsedCategoriaId,
            email: formData.email?.trim() || null,
            fone: formData.fone?.trim() || null,
            obs: formData.obs?.trim() || null,
            situacao_cadastral: isAtivo ? 'ATIVO' : 'INATIVO',
            created_at: new Date().toISOString()
          };
          const { data: retryData, error: retryErr } = await supabase.from('pessoa').insert([basePayload]).select().single();
          if (retryErr) throw retryErr;
          data = retryData;
        }
        
        if (!data?.id) throw new Error("Não foi possível obter o ID da pessoa cadastrada.");
        savedPessoaId = data.id;
      }

      if (!savedPessoaId) throw new Error("ID de pessoa inválido.");

      if (!isAtivo) {
        await pessoaCascadeService.desativarVinculosPessoa(savedPessoaId);
      }

      // Save Grupos
      try {
        await supabase.from('pessoa_grupo').delete().eq('pessoa_id', savedPessoaId);
        if (selectedGrupos.length > 0) {
          const gruposPayload = selectedGrupos.map(gId => ({ pessoa_id: savedPessoaId, grupo_pessoa_id: gId }));
          await supabase.from('pessoa_grupo').insert(gruposPayload);
        }
      } catch (grpErr) {
        console.warn('Erro ao salvar grupos:', grpErr);
      }

      // Save Contatos
      try {
        await supabase.from('pessoa_contato').delete().eq('pessoa_id', savedPessoaId);
        if (contatos.length > 0) {
          const contatosPayload = contatos.map(c => ({
            pessoa_id: savedPessoaId,
            nome: c.nome,
            email: c.email,
            telefone: c.telefone,
            obs: c.obs
          }));
          await supabase.from('pessoa_contato').insert(contatosPayload);
        }
      } catch (contErr) {
        console.warn('Erro ao salvar contatos:', contErr);
      }

      // Save Enderecos
      try {
        await supabase.from('pessoa_endereco').delete().eq('pessoa_id', savedPessoaId);
        if (enderecos.length > 0) {
          const enderecosPayload = enderecos
            .map(e => {
              const cidId = parseInt(e.cidade_id || e.cidade?.id);
              if (isNaN(cidId)) return null;
              return {
                pessoa_id: savedPessoaId,
                cidade_id: cidId,
                nome: e.nome || 'Principal',
                principal: e.principal || 'S',
                // CEP salvo só com dígitos: a Focus NFe (CT-e/NFS-e) exige o
                // valor puro, sem máscara ("01310100", não "01310-100").
                cep: e.cep ? e.cep.replace(/\D/g, '') : null,
                rua: e.rua || null,
                numero: e.numero || null,
                bairro: e.bairro || null,
                complemento: e.complemento || null
              };
            })
            .filter(Boolean);
          if (enderecosPayload.length > 0) {
            await supabase.from('pessoa_endereco').insert(enderecosPayload);
          }
        }
      } catch (endErr) {
        console.warn('Erro ao salvar endereços:', endErr);
      }

      // Save Dados Bancarios
      try {
        await supabase.from('dados_bancarios').delete().eq('pessoa_id', savedPessoaId);
        if (dadosBancarios.length > 0) {
          const bancosPayload = dadosBancarios
            .map(d => {
              const fId = parseInt(d.forma_pagamento_id || d.forma_pagamento?.id);
              const bId = parseInt(d.banco_id || d.banco?.id);
              if (isNaN(fId) || isNaN(bId)) return null;
              return {
                pessoa_id: savedPessoaId,
                forma_pagamento_id: fId,
                banco_id: bId,
                conta: d.conta || null,
                agencia: d.agencia || null,
                nome: d.nome || null,
                cpf: d.cpf || null,
                pix: d.pix || null
              };
            })
            .filter(Boolean);
          if (bancosPayload.length > 0) {
            await supabase.from('dados_bancarios').insert(bancosPayload);
          }
        }
      } catch (bancErr) {
        console.warn('Erro ao salvar dados bancários:', bancErr);
      }

      onSave();
    } catch (error: any) {
      console.error('Error saving:', error);
      alert('Erro ao salvar: ' + (error?.message || 'Verifique os dados digitados e tente novamente.'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
        <div className="bg-white p-6 rounded-xl shadow-xl flex items-center gap-4">
          <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-lg font-medium text-slate-700">Carregando dados...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 max-w-5xl w-full flex">
        <div className="w-full h-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-200">
          
          {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-xl font-bold text-slate-800">
            {pessoaId ? 'Editar Pessoa' : 'Cadastro de Pessoa'}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 hover:bg-slate-200 p-2 rounded-md transition-colors flex items-center gap-2">
            <X className="w-5 h-5" />
            <span className="text-sm font-medium">Fechar</span>
          </button>
        </div>

        {/* Stepper */}
        <div className="bg-white border-b border-slate-200 px-6 py-6">
          <div className="flex items-center justify-between max-w-3xl mx-auto relative">
            <div className="absolute left-0 top-5 w-full h-0.5 bg-slate-200 -z-10"></div>
            <div className="absolute left-0 top-5 h-0.5 bg-red-600 -z-10 transition-all duration-300" style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}></div>
            
            {steps.map((step, index) => (
              <div key={step.id} className="flex flex-col items-center relative z-10 bg-white px-2 cursor-pointer" onClick={() => setCurrentStep(index)}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors duration-300 ${currentStep === index ? 'border-red-600 bg-red-600 text-white' : currentStep > index ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-300 bg-white text-slate-400'}`}>
                  {currentStep > index ? <Check className="w-5 h-5" /> : <step.icon className="w-5 h-5" />}
                </div>
                <span className={`text-xs mt-2 font-medium ${currentStep === index ? 'text-red-600' : currentStep > index ? 'text-slate-800' : 'text-slate-500'}`}>{step.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
          
          {/* TAB: DADOS GERAIS */}
          {currentStep === 0 && (
            <div className="space-y-8 max-w-4xl mx-auto">
              {/* Tipo de Pessoa Toggle */}
              <div className="flex gap-4">
                <button type="button" onClick={() => handleTipoPessoaChange('PF')} className={`flex-1 py-4 rounded-xl border-2 flex flex-col items-center justify-center gap-2 transition-all ${tipoPessoa === 'PF' ? 'border-red-600 bg-red-50 text-red-700' : 'border-slate-200 bg-white text-slate-500 hover:border-red-200'}`}>
                  <User className="w-6 h-6" />
                  <span className="font-semibold">Pessoa Física</span>
                </button>
                <button type="button" onClick={() => handleTipoPessoaChange('PJ')} className={`flex-1 py-4 rounded-xl border-2 flex flex-col items-center justify-center gap-2 transition-all ${tipoPessoa === 'PJ' ? 'border-red-600 bg-red-50 text-red-700' : 'border-slate-200 bg-white text-slate-500 hover:border-red-200'}`}>
                  <Building2 className="w-6 h-6" />
                  <span className="font-semibold">Pessoa Jurídica</span>
                </button>
              </div>

              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <label className="block text-sm font-medium text-slate-700 mb-2">Grupos:</label>
                <div className="flex flex-wrap gap-2">
                  {gruposPessoa.map(g => {
                    const isSelected = selectedGrupos.includes(g.id);
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => handleGrupoChange(g.id)}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                          isSelected 
                            ? 'bg-red-600 text-white border-red-600' 
                            : 'bg-white text-slate-700 border-slate-300 hover:border-red-400'
                        }`}
                      >
                        {g.nome}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Seção CNH (Condicional) */}
              {isMotorista() && (
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 shadow-sm animate-in fade-in slide-in-from-bottom-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-slate-800">Dados do Motorista (CNH)</h3>
                    <div className="flex items-center gap-2">
                      <input 
                        type="file" 
                        accept="image/*,application/pdf" 
                        className="hidden" 
                        ref={fileInputRef}
                        onChange={handleCnhUpload}
                      />
                      <button 
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isAnalyzingCnh}
                        className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors shadow-sm disabled:opacity-70"
                      >
                        {isAnalyzingCnh ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : (
                          <Camera className="w-4 h-4" />
                        )}
                        {isAnalyzingCnh ? 'Analisando CNH...' : 'Ler CNH com IA'}
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Número CNH:</label>
                      <input type="text" name="numero_cnh" value={formData.numero_cnh} onChange={handleInputChange} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Categoria:</label>
                      <input type="text" name="categoria_cnh" value={formData.categoria_cnh} onChange={handleInputChange} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Vencimento:</label>
                      <input type="date" name="vencimento_cnh" value={formData.vencimento_cnh} onChange={handleInputChange} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500 bg-white" />
                    </div>
                  </div>
                  
                  {formData.documento_cnh && (
                    <div className="mt-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Documento Anexado</p>
                      {formData.documento_cnh.toLowerCase().endsWith('.pdf') ? (
                        <a href={formData.documento_cnh} target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-700 hover:underline transition-colors">
                          Visualizar PDF
                        </a>
                      ) : (
                        <img src={formData.documento_cnh} alt="CNH" className="h-32 object-contain rounded border border-slate-200 bg-white p-1" />
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Seção 1: Info Principal */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <div className="md:col-span-2 flex items-center gap-4">
                  <div className="w-32">
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">ID:</label>
                    <input type="text" disabled value={formData.id} className="w-full bg-slate-100 border border-slate-200 rounded-md py-2 px-3 text-slate-500 cursor-not-allowed" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-red-600 mb-1">{docLabel}: *</label>
                    <div className="flex gap-2">
                      <input type="text" name="documento" value={formData.documento} onChange={handleInputChange}  className={`flex-1 border border-slate-300 rounded-md py-2 px-3 focus:ring-red-500 focus:border-red-500 outline-none `} placeholder={docPlaceholder} />
                      {isPJ && (
                        <button type="button" onClick={buscarCNPJ} className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-md hover:bg-slate-50 transition-colors shadow-sm whitespace-nowrap">
                          <Search className="w-4 h-4" /> Buscar CNPJ
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-red-600 mb-1">Tipo de Pessoa / Cliente: *</label>
                  <select 
                    name="tipo_cliente_id" 
                    value={formData.tipo_cliente_id} 
                    onChange={e => {
                      const val = e.target.value;
                      setFormData(prev => ({ ...prev, tipo_cliente_id: val }));
                      const selTipo = tiposCliente.find(t => t.id.toString() === val);
                      if (selTipo) {
                        const nome = (selTipo.nome || '').toLowerCase();
                        const sigla = (selTipo.sigla || '').toLowerCase();
                        if (sigla === 'pj' || nome.includes('jurid') || nome.includes('pj') || nome.includes('empresa')) {
                          setTipoPessoa('PJ');
                        } else {
                          setTipoPessoa('PF');
                        }
                      }
                    }} 
                     
                    className={`w-full border border-slate-300 rounded-md py-2 px-3 focus:ring-red-500 focus:border-red-500 outline-none bg-white font-medium `}
                  >
                    <option value="">Selecione...</option>
                    {tiposCliente.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.nome} {t.sigla ? `(${t.sigla})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Categoria:</label>
                  <select name="categoria_cliente_id" value={formData.categoria_cliente_id} onChange={handleInputChange}  className={`w-full border border-slate-300 rounded-md py-2 px-3 focus:ring-red-500 focus:border-red-500 outline-none bg-white `}>
                    <option value="">Selecione...</option>
                    {categoriasCliente.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Status do Cadastro (Banco):</label>
                  <div className="flex gap-2 max-w-sm">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, ativo: true }))}
                      className={`flex-1 py-2 px-3 rounded-lg border flex items-center justify-center gap-1.5 text-sm font-semibold transition-colors cursor-pointer ${
                        formData.ativo
                          ? 'bg-emerald-50 border-emerald-500 text-emerald-700 shadow-xs'
                          : 'bg-white border-slate-300 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Ativo
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, ativo: false }))}
                      className={`flex-1 py-2 px-3 rounded-lg border flex items-center justify-center gap-1.5 text-sm font-semibold transition-colors cursor-pointer ${
                        !formData.ativo
                          ? 'bg-rose-50 border-rose-500 text-rose-700 shadow-xs'
                          : 'bg-white border-slate-300 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      <XCircle className="w-4 h-4 text-rose-600" />
                      Inativo
                    </button>
                  </div>
                </div>

                <div className={isPJ ? "" : "md:col-span-2"}>
                  <label className="block text-sm font-medium text-red-600 mb-1">{isPJ ? "Razão Social: *" : "Nome: *"}</label>
                  <input type="text" name="nome" value={formData.nome} onChange={handleInputChange} className="w-full border border-slate-300 rounded-md py-2 px-3 focus:ring-red-500 focus:border-red-500 outline-none" />
                </div>

                {isPJ && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nome Fantasia:</label>
                    <input type="text" name="nome_fantasia" value={formData.nome_fantasia} onChange={handleInputChange} className="w-full border border-slate-300 rounded-md py-2 px-3 focus:ring-red-500 focus:border-red-500 outline-none" />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email:</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full border border-slate-300 rounded-md py-2 px-3 focus:ring-red-500 focus:border-red-500 outline-none" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fone:</label>
                  <input type="text" name="fone" value={formData.fone} onChange={handleInputChange} className="w-full border border-slate-300 rounded-md py-2 px-3 focus:ring-red-500 focus:border-red-500 outline-none" placeholder="(00) 00000-0000" />
                </div>

                <div className="md:col-span-2 grid gap-2 sm:grid-cols-2">
                  <label className="flex items-start gap-2.5 p-3 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.aceita_notificacao_whatsapp}
                      onChange={(e) => setFormData(prev => ({ ...prev, aceita_notificacao_whatsapp: e.target.checked }))}
                      className="mt-0.5 w-4 h-4 accent-emerald-600"
                    />
                    <span className="text-sm text-slate-700">
                      <span className="font-semibold">Aceita notificações via WhatsApp</span> (ex: OS pronta para assinatura, status). Marque apenas com autorização do cliente para o número acima.
                    </span>
                  </label>
                  <label className="flex items-start gap-2.5 p-3 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.aceita_notificacao_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, aceita_notificacao_email: e.target.checked }))}
                      className="mt-0.5 w-4 h-4 accent-blue-600"
                    />
                    <span className="text-sm text-slate-700">
                      <span className="font-semibold">Aceita notificações por e-mail</span> (mesmos avisos, enviados para o e-mail acima). Marque apenas com autorização do cliente.
                    </span>
                  </label>
                </div>

                {isPJ && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">CNAE:</label>
                      <input type="text" name="cnae" value={formData.cnae} onChange={handleInputChange}  className={`w-full border border-slate-300 rounded-md py-2 px-3 focus:ring-red-500 focus:border-red-500 outline-none `} />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Situação Cadastral:</label>
                      <input type="text" name="situacao_cadastral" value={formData.situacao_cadastral} onChange={handleInputChange}  className={`w-full border border-slate-300 rounded-md py-2 px-3 focus:ring-red-500 focus:border-red-500 outline-none `} />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Data Início Atividade:</label>
                      <input type="text" name="data_inicio_atividade" value={formData.data_inicio_atividade} onChange={handleInputChange}  className={`w-full border border-slate-300 rounded-md py-2 px-3 focus:ring-red-500 focus:border-red-500 outline-none `} placeholder="DD/MM/YYYY" />
                    </div>
                  </>
                )}

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Obs:</label>
                  <textarea name="obs" value={formData.obs} onChange={handleInputChange}  rows={3} className={`w-full border border-slate-300 rounded-md py-2 px-3 focus:ring-red-500 focus:border-red-500 outline-none `}></textarea>
                </div>
              </div>

            </div>
          )}

          {/* TAB: CONTATOS */}
          {currentStep === 1 && (
            <div className="space-y-8 max-w-4xl mx-auto">
              {/* Seção Contatos */}
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <h3 className="text-lg font-bold text-slate-800 mb-4 border-b pb-2">Contatos</h3>
                
                <div className="flex items-end gap-4 mb-6">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nome</label>
                    <input type="text" value={newContato.nome} onChange={e => setNewContato({...newContato, nome: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input type="email" value={newContato.email} onChange={e => setNewContato({...newContato, email: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Telefone</label>
                    <input type="text" value={newContato.telefone} onChange={e => setNewContato({...newContato, telefone: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Obs</label>
                    <input type="text" value={newContato.obs} onChange={e => setNewContato({...newContato, obs: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500" />
                  </div>
                  <button type="button" onClick={addContato} className="bg-red-50 border border-red-200 text-red-700 p-2.5 rounded-md hover:bg-red-100 transition-colors">
                    <Plus className="w-5 h-5" />
                  </button>
                </div>

                {contatos.length > 0 && (
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-slate-500">Nome</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-500">Email</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-500">Telefone</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-500">Obs</th>
                        <th className="px-4 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {contatos.map((c, i) => (
                        <tr key={c.id || i}>
                          <td className="px-4 py-2">{c.nome}</td>
                          <td className="px-4 py-2">{c.email}</td>
                          <td className="px-4 py-2">{c.telefone}</td>
                          <td className="px-4 py-2">{c.obs}</td>
                          <td className="px-4 py-2 text-right">
                            <button type="button" onClick={() => removeContato(i)} className="text-red-500 hover:text-red-700 p-1 bg-red-50 rounded">
                              <X className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* TAB: ENDEREÇOS */}
          {currentStep === 2 && (
            <div className="space-y-8 max-w-4xl mx-auto">
              {/* Seção Endereços */}
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <h3 className="text-lg font-bold text-slate-800 mb-4 border-b pb-2">Endereços</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nome do Endereço</label>
                    <input type="text" value={newEndereco.nome} onChange={e => setNewEndereco({...newEndereco, nome: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500" placeholder="Ex: Sede, Galpão..." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Principal:</label>
                    <select value={newEndereco.principal} onChange={e => setNewEndereco({...newEndereco, principal: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 bg-white outline-none focus:ring-red-500 focus:border-red-500">
                      <option value="S">Sim</option>
                      <option value="N">Não</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">CEP:</label>
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        value={newEndereco.cep} 
                        onChange={e => {
                          const val = e.target.value;
                          setNewEndereco({...newEndereco, cep: val});
                          if (val.replace(/\D/g, '').length === 8) {
                            buscarCEP(val);
                          }
                        }} 
                        className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500" 
                      />
                      <button type="button" onClick={() => buscarCEP()} className="bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded-md hover:bg-slate-50 flex items-center gap-1 transition-colors">
                        <Search className="w-4 h-4" /> Buscar
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-600 mb-1">Estado:</label>
                    <select 
                      value={newEndereco.estado_id} 
                      onChange={e => {
                        const estado_id = e.target.value;
                        setNewEndereco({...newEndereco, estado_id, cidade_id: ''});
                        if (estado_id) {
                          fetchCidades(estado_id);
                        } else {
                          setCidades([]);
                        }
                      }} 
                      className="w-full border border-slate-300 rounded-md py-2 px-3 bg-white outline-none focus:ring-red-500 focus:border-red-500"
                    >
                      <option value="">Selecionar</option>
                      {estados.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-600 mb-1">Cidade:</label>
                    <select value={newEndereco.cidade_id} onChange={e => setNewEndereco({...newEndereco, cidade_id: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 bg-white outline-none focus:ring-red-500 focus:border-red-500" disabled={!newEndereco.estado_id}>
                      <option value="">Selecionar</option>
                      {cidades.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Rua:</label>
                    <input type="text" value={newEndereco.rua} onChange={e => setNewEndereco({...newEndereco, rua: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Número:</label>
                    <input type="text" value={newEndereco.numero} onChange={e => setNewEndereco({...newEndereco, numero: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Bairro:</label>
                    <input type="text" value={newEndereco.bairro} onChange={e => setNewEndereco({...newEndereco, bairro: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Complemento:</label>
                    <input type="text" value={newEndereco.complemento} onChange={e => setNewEndereco({...newEndereco, complemento: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500" />
                  </div>
                </div>
                
                <button type="button" onClick={addEndereco} className="flex items-center gap-2 bg-white border border-red-500 text-red-600 px-4 py-2 rounded-md hover:bg-red-50 transition-colors mb-6">
                  <Plus className="w-4 h-4" /> Adicionar
                </button>

                {enderecos.length > 0 && (
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-slate-500">Nome</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-500">Cidade</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-500">Rua</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-500">Número</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-500">Bairro</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-500">Principal</th>
                        <th className="px-4 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {enderecos.map((e, i) => (
                        <tr key={e.id || i}>
                          <td className="px-4 py-2">{e.nome}</td>
                          <td className="px-4 py-2">{e.cidade?.nome || cidades.find(c=>c.id.toString()===e.cidade_id)?.nome}</td>
                          <td className="px-4 py-2">{e.rua}</td>
                          <td className="px-4 py-2">{e.numero}</td>
                          <td className="px-4 py-2">{e.bairro}</td>
                          <td className="px-4 py-2">{e.principal === 'S' ? 'Sim' : 'Não'}</td>
                          <td className="px-4 py-2 text-right">
                            <button type="button" onClick={() => removeEndereco(i)} className="text-red-500 hover:text-red-700 p-1 bg-red-50 rounded">
                              <X className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* TAB: DADOS BANCÁRIOS */}
          {currentStep === 3 && (
            <div className="space-y-8 max-w-4xl mx-auto">
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-red-600 mb-1">Forma pagamento:</label>
                  <select value={newDadoBancario.forma_pagamento_id} onChange={e => setNewDadoBancario({...newDadoBancario, forma_pagamento_id: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 bg-white outline-none focus:ring-red-500 focus:border-red-500">
                    <option value="">Selecionar</option>
                    {formasPagamento.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-red-600 mb-1">Banco:</label>
                  <select value={newDadoBancario.banco_id} onChange={e => setNewDadoBancario({...newDadoBancario, banco_id: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 bg-white outline-none focus:ring-red-500 focus:border-red-500">
                    <option value="">Selecionar</option>
                    {bancos.map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Conta:</label>
                  <input type="text" value={newDadoBancario.conta} onChange={e => setNewDadoBancario({...newDadoBancario, conta: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Agência:</label>
                  <input type="text" value={newDadoBancario.agencia} onChange={e => setNewDadoBancario({...newDadoBancario, agencia: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Favorecido:</label>
                  <input type="text" value={newDadoBancario.nome} onChange={e => setNewDadoBancario({...newDadoBancario, nome: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">CPF ou CNPJ:</label>
                  <input type="text" value={newDadoBancario.cpf} onChange={e => setNewDadoBancario({...newDadoBancario, cpf: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Chave Pix:</label>
                  <input type="text" value={newDadoBancario.pix} onChange={e => setNewDadoBancario({...newDadoBancario, pix: e.target.value})} className="w-full border border-slate-300 rounded-md py-2 px-3 outline-none focus:ring-red-500 focus:border-red-500" />
                </div>
              </div>

              <button type="button" onClick={addDadoBancario} className="flex items-center gap-2 bg-white border border-red-500 text-red-600 px-4 py-2 rounded-md hover:bg-red-50 transition-colors mb-6">
                <Plus className="w-4 h-4" /> Adicionar
              </button>

              {dadosBancarios.length > 0 && (
                <table className="min-w-full divide-y divide-slate-200 text-sm mt-4">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-slate-500">Forma Pagamento</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-500">Banco</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-500">Agência</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-500">Conta</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-500">Nome</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-500">CPF ou CNPJ</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-500">Chave Pix</th>
                      <th className="px-4 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {dadosBancarios.map((d, i) => (
                      <tr key={d.id || i}>
                        <td className="px-4 py-2">{d.forma_pagamento?.nome || formasPagamento.find(f=>f.id.toString()===d.forma_pagamento_id)?.nome}</td>
                        <td className="px-4 py-2">{d.banco?.nome || bancos.find(b=>b.id.toString()===d.banco_id)?.nome}</td>
                        <td className="px-4 py-2">{d.agencia}</td>
                        <td className="px-4 py-2">{d.conta}</td>
                        <td className="px-4 py-2">{d.nome}</td>
                        <td className="px-4 py-2">{d.cpf}</td>
                        <td className="px-4 py-2">{d.pix}</td>
                        <td className="px-4 py-2 text-right">
                          <button type="button" onClick={() => removeDadoBancario(i)} className="text-red-500 hover:text-red-700 p-1 bg-red-50 rounded">
                            <X className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              </div>
            </div>
          )}

          {/* TAB: ACESSO E CREDENCIAIS DE USUÁRIO */}
          {currentStep === 4 && (
            <div className="space-y-6 max-w-4xl mx-auto">
              
              {/* Mensagem de Feedback */}
              {accessMsg && (
                <div className={`p-4 rounded-xl border flex items-center justify-between gap-3 ${
                  accessMsg.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'
                }`}>
                  <div className="flex items-center gap-2">
                    {accessMsg.type === 'success' ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertCircle className="w-5 h-5 text-rose-600" />}
                    <span className="text-sm font-medium">{accessMsg.text}</span>
                  </div>
                  <button type="button" onClick={() => setAccessMsg(null)} className="text-xs font-semibold opacity-70 hover:opacity-100">
                    Fechar
                  </button>
                </div>
              )}

              {/* Aviso: vínculo encontrado só pelo nome (nomes duplicados podem linkar a pessoa errada) */}
              {!isCheckingAccess && linkedUsers.length > 0 && matchedByNameOnly && (
                <div className="bg-amber-50 border border-amber-300 text-amber-900 p-3 rounded-xl text-xs font-semibold flex items-start gap-2">
                  <span>⚠️</span>
                  <span>
                    Esta conta foi encontrada apenas pelo NOME (não bateu e-mail nem documento). Nomes duplicados existem —
                    confirme com cuidado que esta é realmente a pessoa certa antes de alterar grupo ou senha.
                  </span>
                </div>
              )}

              {/* Seletor de Contas Vinculadas (Caso existam várias) */}
              {!isCheckingAccess && linkedUsers.length > 0 && (
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center gap-2">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider mr-2">
                    Contas Encontradas ({linkedUsers.length}):
                  </span>
                  {linkedUsers.map((u, idx) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => handleSelectUserIndex(idx)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-2 border ${
                        selectedUserIdx === idx
                          ? 'bg-red-600 text-white border-red-600 shadow-xs'
                          : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      <UserCheck className="w-3.5 h-3.5" />
                      <span>
                        {u.login || u.name} <span className="opacity-75 font-mono">(#{u.id})</span>
                      </span>
                      <span className={`w-2 h-2 rounded-full ${u.active === 'Y' || u.active === 'S' ? 'bg-emerald-400' : 'bg-rose-400'}`}></span>
                    </button>
                  ))}

                  <button
                    type="button"
                    onClick={() => handleSelectUserIndex(-1)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1 border ${
                      selectedUserIdx === -1
                        ? 'bg-slate-800 text-white border-slate-800'
                        : 'bg-white text-slate-600 border-dashed border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Nova Conta</span>
                  </button>
                </div>
              )}

              {isCheckingAccess ? (
                <div className="bg-white p-12 rounded-xl border border-slate-200 shadow-sm flex flex-col items-center justify-center gap-3">
                  <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-slate-600 text-sm font-medium">Verificando conta de acesso do sistema...</p>
                </div>
              ) : linkedUser ? (
                /* CASO A: USUÁRIO ENCONTRADO / CONTA EXISTENTE */
                <div className="space-y-6">
                  {/* Card de Status do Usuário */}
                  <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-5">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700 font-bold text-lg">
                          <UserCheck className="w-6 h-6 text-slate-700" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-bold text-slate-800">{linkedUser.name}</h3>
                            <span className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200">
                              ID #{linkedUser.id}
                            </span>
                          </div>
                          <p className="text-sm text-slate-500 font-medium mt-0.5">
                            E-mail: <strong className="text-slate-700">{linkedUser.email || 'Não cadastrado'}</strong> | Login: <strong className="text-slate-700">{linkedUser.login}</strong>
                          </p>
                        </div>
                      </div>

                      {/* Status & Botão Toggle */}
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 text-xs font-bold rounded-full flex items-center gap-1.5 border ${
                          linkedUser.active === 'Y' || linkedUser.active === 'S'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                            : 'bg-rose-50 text-rose-700 border-rose-300'
                        }`}>
                          <span className={`w-2 h-2 rounded-full ${linkedUser.active === 'Y' || linkedUser.active === 'S' ? 'bg-emerald-600' : 'bg-rose-600'}`}></span>
                          {linkedUser.active === 'Y' || linkedUser.active === 'S' ? 'Acesso Ativo' : 'Acesso Inativo'}
                        </span>

                        <button
                          type="button"
                          onClick={handleToggleAccessStatus}
                          disabled={isSavingAccess}
                          className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors flex items-center gap-1.5 ${
                            linkedUser.active === 'Y' || linkedUser.active === 'S'
                              ? 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
                              : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                          }`}
                        >
                          <Power className="w-3.5 h-3.5" />
                          {linkedUser.active === 'Y' || linkedUser.active === 'S' ? 'Inativar Acesso' : 'Ativar Acesso'}
                        </button>
                      </div>
                    </div>

                    {/* Perfis Vinculados */}
                    <div className="mt-4 pt-2">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">Perfis / Grupos de Acesso Atuais:</span>
                      <div className="flex flex-wrap gap-2">
                        {linkedUserGroups.length > 0 ? (
                          linkedUserGroups.map(g => (
                            <span key={g.id} className="px-3 py-1 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs font-semibold flex items-center gap-1">
                              <ShieldCheck className="w-3.5 h-3.5 text-red-600" />
                              {g.name}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-slate-500 italic">Sem grupo vinculado</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Alterar Grupo de Acesso */}
                  <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <h4 className="text-base font-bold text-slate-800 mb-1 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-red-600" /> Alterar Grupo / Perfil de Acesso
                    </h4>
                    <p className="text-xs text-slate-500 mb-4">Selecione o perfil que define os privilégios e telas que esta conta pode acessar no sistema.</p>

                    <div className="flex flex-col md:flex-row gap-3 items-end">
                      <div className="flex-1">
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Perfil de Acesso (System Group):</label>
                        <select
                          value={editingGroupId}
                          onChange={e => setEditingGroupId(Number(e.target.value))}
                          className="w-full border border-slate-300 rounded-lg py-2 px-3 text-sm bg-white focus:ring-red-500 focus:border-red-500 outline-none"
                        >
                          {allSystemGroups.map(g => (
                            <option key={g.id} value={g.id}>
                              {g.name} (ID #{g.id})
                            </option>
                          ))}
                        </select>
                      </div>

                      <button
                        type="button"
                        onClick={handleUpdateUserGroup}
                        disabled={isSavingAccess}
                        className="bg-slate-800 hover:bg-slate-900 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors shadow-sm whitespace-nowrap disabled:opacity-50"
                      >
                        Salvar Novo Perfil
                      </button>
                    </div>
                  </div>

                  {/* Alterar Senha */}
                  <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <h4 className="text-base font-bold text-slate-800 mb-1 flex items-center gap-2">
                      <Key className="w-4 h-4 text-red-600" /> Redefinir Senha de Acesso
                    </h4>
                    <p className="text-xs text-slate-500 mb-4">Insira uma nova senha para atualizar as credenciais do usuário.</p>

                    <div className="flex flex-col md:flex-row gap-3 items-end">
                      <div className="flex-1 relative">
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Nova Senha:</label>
                        <div className="relative">
                          <input
                            type={showNewPasswordEdit ? 'text' : 'password'}
                            value={newPasswordEdit}
                            onChange={e => setNewPasswordEdit(e.target.value)}
                            placeholder="Mínimo 6 caracteres"
                            className="w-full border border-slate-300 rounded-lg py-2 pl-3 pr-10 text-sm bg-white focus:ring-red-500 focus:border-red-500 outline-none"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPasswordEdit(!showNewPasswordEdit)}
                            className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-600"
                          >
                            {showNewPasswordEdit ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleChangePassword}
                        disabled={isChangingPassword || !newPasswordEdit}
                        className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors shadow-sm whitespace-nowrap disabled:opacity-50 flex items-center gap-2"
                      >
                        {isChangingPassword ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        Salvar Nova Senha
                      </button>
                    </div>
                  </div>

                  {/* Remover Acesso */}
                  <div className="bg-rose-50/60 p-5 rounded-xl border border-rose-200 flex items-center justify-between">
                    <div>
                      <h5 className="text-sm font-bold text-rose-800">Remover Acesso do Sistema</h5>
                      <p className="text-xs text-rose-600 mt-0.5">Exclui o login e perfil do usuário do sistema para esta pessoa.</p>
                    </div>

                    <button
                      type="button"
                      onClick={handleDeleteAccess}
                      disabled={isSavingAccess}
                      className="bg-white border border-rose-300 text-rose-700 hover:bg-rose-100 px-4 py-2 rounded-lg text-xs font-bold transition-colors shadow-xs flex items-center gap-1.5"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Excluir Conta
                    </button>
                  </div>

                </div>
              ) : (
                /* CASO B: NENHUM USUÁRIO VINCULADO / FORMAR CONTA DE ACESSO */
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-6">
                  
                  {/* Banner Informativo */}
                  <div className="bg-slate-50 border border-slate-200 p-5 rounded-xl flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center text-red-600 flex-shrink-0">
                      <Lock className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-800">Conta de Acesso ao Sistema Não Configurada</h3>
                      <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                        Esta pessoa ainda não possui credenciais ativas para login no painel ou aplicativos (Área do Cliente / Área do Motorista). Preencha o formulário abaixo para criar a conta.
                      </p>
                    </div>
                  </div>

                  {/* Formulário de Criação */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-bold text-slate-800 border-b border-slate-100 pb-2">
                      Dados para Cadastro do Login
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-red-600 mb-1">Nome do Usuário: *</label>
                        <input
                          type="text"
                          value={accessForm.name}
                          onChange={e => setAccessForm(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full border border-slate-300 rounded-md py-2 px-3 text-sm focus:ring-red-500 focus:border-red-500 outline-none"
                          placeholder="Ex: João da Silva"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-red-600 mb-1">Login / Nome de Usuário: *</label>
                        <input
                          type="text"
                          value={accessForm.login}
                          onChange={e => setAccessForm(prev => ({ ...prev, login: e.target.value }))}
                          className="w-full border border-slate-300 rounded-md py-2 px-3 text-sm focus:ring-red-500 focus:border-red-500 outline-none"
                          placeholder="Ex: joaosilva ou e-mail"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-red-600 mb-1">E-mail de Login / Acesso: *</label>
                        <input
                          type="email"
                          value={accessForm.email}
                          onChange={e => setAccessForm(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full border border-slate-300 rounded-md py-2 px-3 text-sm focus:ring-red-500 focus:border-red-500 outline-none"
                          placeholder="Ex: cliente@empresa.com"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-red-600 mb-1">Perfil / Grupo de Permissão: *</label>
                        <select
                          value={accessForm.groupId}
                          onChange={e => setAccessForm(prev => ({ ...prev, groupId: Number(e.target.value) }))}
                          className="w-full border border-slate-300 rounded-md py-2 px-3 text-sm bg-white focus:ring-red-500 focus:border-red-500 outline-none font-medium"
                        >
                          {allSystemGroups.map(g => (
                            <option key={g.id} value={g.id}>
                              {g.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Senha Inicial de Acesso:</label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <input
                              type={accessForm.showPassword ? 'text' : 'password'}
                              value={accessForm.password}
                              onChange={e => setAccessForm(prev => ({ ...prev, password: e.target.value }))}
                              className="w-full border border-slate-300 rounded-md py-2 pl-3 pr-10 text-sm focus:ring-red-500 focus:border-red-500 outline-none"
                            />
                            <button
                              type="button"
                              onClick={() => setAccessForm(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                              className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-600"
                            >
                              {accessForm.showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              const randomPass = 'Voope@' + Math.floor(1000 + Math.random() * 9000);
                              setAccessForm(prev => ({ ...prev, password: randomPass, showPassword: true }));
                            }}
                            className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-3 py-2 rounded-md text-xs font-semibold transition-colors flex items-center gap-1 whitespace-nowrap"
                          >
                            <RefreshCw className="w-3.5 h-3.5" /> Gerar Senha
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-slate-100 flex justify-end">
                      <button
                        type="button"
                        onClick={handleCreateAccess}
                        disabled={isSavingAccess}
                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-lg text-sm font-bold transition-all shadow-md flex items-center gap-2 disabled:opacity-70"
                      >
                        {isSavingAccess ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : (
                          <ShieldCheck className="w-5 h-5" />
                        )}
                        Criar e Habilitar Acesso
                      </button>
                    </div>
                  </div>

                </div>
              )}

            </div>
          )}

        </div>

        {/* Footer Actions */}
        <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setCurrentStep(p => Math.max(0, p - 1))}
            disabled={currentStep === 0}
            className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 px-6 py-2.5 rounded-md hover:bg-slate-50 font-medium transition-colors shadow-sm disabled:opacity-50"
          >
            <ChevronLeft className="w-5 h-5" />
            Anterior
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                if(window.confirm('Limpar formulário?')) {
                  setFormData({ id: '', nome: '', nome_fantasia: '', documento: '', tipo_cliente_id: '', categoria_cliente_id: '', email: '', fone: '', obs: '', numero_cnh: '', categoria_cnh: '', vencimento_cnh: '', documento_cnh: '', cnae: '', situacao_cadastral: '', data_inicio_atividade: '', ativo: true, aceita_notificacao_whatsapp: false, aceita_notificacao_email: false });
                  setSelectedGrupos([]); setContatos([]); setEnderecos([]); setDadosBancarios([]);
                  setCurrentStep(0);
                }
              }}
              className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 px-4 py-2.5 rounded-md hover:bg-slate-50 font-medium transition-colors shadow-sm"
            >
              Limpar formulário
            </button>
            
            {currentStep < steps.length - 1 ? (
              <button
                type="button"
                onClick={() => setCurrentStep(p => Math.min(steps.length - 1, p + 1))}
                className="flex items-center gap-2 bg-red-600 text-white px-6 py-2.5 rounded-md hover:bg-red-700 font-medium transition-colors shadow-sm"
              >
                Próximo
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 bg-red-600 text-white px-6 py-2.5 rounded-md hover:bg-red-700 font-medium transition-colors shadow-sm disabled:opacity-70"
              >
                {isSaving ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Salvar Cadastro
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
    </div>
  );
}
